/**
 * @fileoverview In-app preview modal for mail attachments (MOR-529).
 *
 * Previews a storage-backed file (signed URL in an iframe — browsers render
 * PDFs/images natively) or inline HTML (the generated Day One letter that
 * travels in the email body). Download button for file-backed previews.
 */

import { Download } from "lucide-react";

import { Button } from "@/ui/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";

export interface AttachmentPreview {
  title: string;
  /** Signed storage URL — file-backed attachments. */
  url?: string | null;
  /** Inline HTML — the generated document appended to the email body. */
  html?: string | null;
}

export function AttachmentPreviewModal({
  preview,
  onClose,
}: {
  preview: AttachmentPreview | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={preview !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex h-[85vh] max-w-4xl flex-col gap-0 p-0">
        <DialogHeader className="border-b px-5 py-3">
          <DialogTitle className="truncate pr-8 text-base">
            {preview?.title}
          </DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 bg-muted/30">
          {preview?.url ? (
            <iframe
              title={preview.title}
              src={preview.url}
              className="size-full border-0 bg-white"
            />
          ) : preview?.html ? (
            <iframe
              title={preview.title}
              sandbox=""
              srcDoc={preview.html}
              className="size-full border-0 bg-white"
            />
          ) : null}
        </div>
        <DialogFooter className="border-t px-5 py-3">
          {preview?.url && (
            <Button asChild size="sm">
              <a href={preview.url} download={preview.title}>
                <Download className="mr-1.5 size-3.5" />
                Download
              </a>
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
